import Image from 'next/image';

import { CreditCard, Languages, MessageCircle, ReceiptText } from 'lucide-react';

import type { MarketingLocale } from '~/lib/marketing/site';

import { GCC_TRUST } from '../../_lib/home.content';

const IMAGE_BASE = '/landing-pages/flow/images';

const ITEM_STYLES = [
  { Icon: Languages, bg: 'bg-[#e6f0ff]', color: '#3f6099' },
  { Icon: CreditCard, bg: 'bg-[#ddf5ec]', color: '#2f7a63' },
  { Icon: ReceiptText, bg: 'bg-[#ede8e2]', color: '#2b2b2b' },
  { Icon: MessageCircle, bg: 'bg-[#e6f0ff]', color: '#3f6099' },
] as const;

const PAYMENT_LOGOS = [
  { src: `${IMAGE_BASE}/Visa_Brandmark_Blue_RGB_2021.png`, alt: 'Visa', width: 64 },
  { src: `${IMAGE_BASE}/ma_symbol.svg`, alt: 'Mastercard', width: 48 },
  { src: `${IMAGE_BASE}/Apple_Pay_Mark_RGB_0419.svg`, alt: 'Apple Pay', width: 56 },
  { src: `${IMAGE_BASE}/Mada - SVG.svg`, alt: 'mada', width: 72 },
] as const;

const PAYMENTS_LABEL = {
  en: 'Accepted through licensed gateways',
  ar: 'مقبولة عبر بوابات دفع مرخصة',
} as const;

/**
 * Mirrors the provider list in home/[account]/accounting (provider-selector).
 * Global systems lead, regional ones follow.
 */
const ACCOUNTING_LOGOS = [
  { src: '/images/accounting/oracle.svg', alt: 'Oracle' },
  { src: '/images/accounting/odoo.svg', alt: 'Odoo' },
  { src: '/images/accounting/zoho-books.png', alt: 'Zoho Books' },
  { src: '/images/accounting/wafeq.png', alt: 'Wafeq' },
  { src: '/images/accounting/qoyod.png', alt: 'Qoyod' },
  { src: '/images/accounting/daftra.svg', alt: 'Daftra', wordmark: true },
] as const;

const ACCOUNTING_LABEL = {
  en: 'Connected to your accounting',
  ar: 'متصلة بنظامك المحاسبي',
} as const;

const EINVOICE_ONLY = {
  en: 'Need ZATCA e-invoicing only? Use eInvoiceMe on its own.',
  ar: 'تحتاج فوترة زاتكا فقط؟ استخدم eInvoiceMe وحدها.',
} as const;

export function MarketReadinessSection({ locale }: { locale: MarketingLocale }) {
  return (
    <section className="bg-white px-4 py-16 md:px-8 md:py-24 lg:px-[90px]">
      <div className="mx-auto max-w-[1320px]">
        <div className="mb-10 text-center md:mb-14">
          <p className="mb-4 font-roboto-mono text-sm uppercase tracking-wider text-[#202020] opacity-80">
            {GCC_TRUST.label[locale]}
          </p>
          <h2
            className="font-roboto text-[28px] font-normal leading-[1.2] text-[#202020] md:text-[34px] lg:text-[40px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {GCC_TRUST.heading[locale]}
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {GCC_TRUST.items.map((item, index) => {
            const { Icon, bg, color } = ITEM_STYLES[index % ITEM_STYLES.length]!;

            return (
              <div key={item.title.en} className="rounded-[16px] border border-[#dcd7cf] bg-[#f7f6f4] p-6">
                <div
                  className={`mb-4 flex h-10 w-10 items-center justify-center rounded-[10px] ${bg}`}
                  aria-hidden="true"
                >
                  <Icon className="h-5 w-5" style={{ color }} />
                </div>
                <h3 className="mb-2 font-roboto text-[18px] font-medium text-[#202020]">
                  {item.title[locale]}
                </h3>
                <p className="font-roboto text-[14px] leading-[22px] text-[#2b2b2b]/70">
                  {item.description[locale]}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 border-t border-[#ebe7e1] pt-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="flex flex-col items-center gap-4">
          <p className="font-roboto-mono text-[11px] uppercase tracking-wide text-[#8a857c]">
            {PAYMENTS_LABEL[locale]}
          </p>
          <ul className="flex flex-wrap items-center justify-center gap-8">
            {PAYMENT_LOGOS.map((logo) => (
              <li key={logo.alt}>
                <Image
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={32}
                  className="h-7 w-auto object-contain"
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col items-center gap-4">
          <p className="font-roboto-mono text-[11px] uppercase tracking-wide text-[#8a857c]">
            {ACCOUNTING_LABEL[locale]}
          </p>
          <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {ACCOUNTING_LOGOS.map((logo) => (
              <li key={logo.alt} className="flex items-center gap-2">
                <Image
                  src={logo.src}
                  alt={'wordmark' in logo ? logo.alt : ''}
                  width={'wordmark' in logo ? 88 : 40}
                  height={'wordmark' in logo ? 32 : 40}
                  className="h-7 w-auto object-contain"
                />
                {!('wordmark' in logo) && (
                  <span className="font-roboto text-[14px] font-medium text-[#202020]">
                    {logo.alt}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="font-roboto text-[13px] text-[#8a857c]">{EINVOICE_ONLY[locale]}</p>
        </div>
        </div>
      </div>
    </section>
  );
}
